import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { useReactFlow } from '@xyflow/react';
import { useState } from 'react';
import AlertDialog from 'src/components/alert-dialog';
import FormSwitch from 'src/components/form-switch';
import { CloseIon } from 'src/components/icons/CloseIcon';
import LoadingSpinner from 'src/components/loading/spinner';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo';
import NetworkIpSubnet from 'src/modules/network-ip-subnet';
import { dispatchNodesChanged } from 'src/utils/dispatch-nodes-changed';
import { twMerge } from 'tailwind-merge';

import {
  EUntaggedVlan,
  IEdgeExtraData,
  EAllowIncomingPackets,
} from '../edges/types';
import { NodeCategoryModel } from '../models/node-data';
import VlanTable from '../vlan-table';

function DialogVlan() {
  const {
    setShowDialogs,
    vlans,
    useCustomVlan,
    globalIpSubnet,
    setUseCustomVlan,
    setVlans,
    setGlobalIpSubnet,
  } = useGlobalContext();

  const [loading, setLoading] = useState(false);

  const { takeSnapshot } = useUndoRedo();

  const [confirmFunction, setConfirmFunction] = useState<
    (() => void) | undefined
  >(undefined);

  const { setNodes, setEdges, getNodes } = useReactFlow();

  const onCancel = () => {
    setShowDialogs({ vlan: false });
  };

  const onChangeUseCustomVlan = (value: boolean) => {
    takeSnapshot();
    if (value) {
      if (
        globalIpSubnet?.ip ||
        globalIpSubnet?.ipSubnet ||
        globalIpSubnet?.subnetMask
      ) {
        setConfirmFunction(() => resetGlobalIpSubnet);
      } else {
        resetGlobalIpSubnet();
      }
    } else if (vlans.length > 0) {
      setConfirmFunction(() => resetVlan);
    } else {
      resetVlan();
    }
  };

  const resetVlan = () => {
    resetNetworkConfiguration();
    setUseCustomVlan?.(false);
  };

  const resetGlobalIpSubnet = () => {
    resetNetworkConfiguration();
    setUseCustomVlan?.(true);
  };

  const resetNetworkConfiguration = () => {
    setVlans([]);
    setGlobalIpSubnet?.({
      ip: '',
      ipSubnet: '',
      subnetMask: '',
    });
    const nodeIds = getNodes().map((n) => n.id);
    setNodes((nodes) =>
      nodes.map((node) => {
        const newData = {
          ...node.data,
          category: {
            ...(node.data?.category as NodeCategoryModel),
            ip: '',
            subnet: '',
            subnetMask: '',
            ipInterfaces: [],
          },
        };

        return {
          ...node,
          data: newData,
        };
      }),
    );
    dispatchNodesChanged(nodeIds);
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        data: {
          ...(edge.data as IEdgeExtraData),
          vlanIds: [],
          untaggedVlan: EUntaggedVlan.AUTO,
          allowIncomingPackets: EAllowIncomingPackets.AUTO,
        },
      })),
    );
  };

  const forceRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 200);
  };

  return (
    <>
      <Dialog
        open
        onClose={onCancel}
        className="relative z-50"
        data-testid="dialog-global-vlan"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className={twMerge([
                'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6',
                'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
                useCustomVlan
                  ? 'max-w-5xl sm:max-w-[96%] md:w-[80vw]'
                  : 'md:w-xl max-w-xl sm:max-w-xl',
              ])}
              data-testid="dialog-global-vlan-panel"
            >
              {loading ? (
                <div className="flex h-full min-h-[200px] min-w-[500px] items-center justify-center">
                  <LoadingSpinner className="h-10 w-10 text-gray-500" />
                </div>
              ) : (
                <>
                  <DialogTitle
                    className="mb-2 border-b py-4 text-xl font-bold"
                    data-testid="dialog-global-vlan-title"
                  >
                    <div className="flex flex-row items-center justify-between gap-2">
                      <h3
                        className="text-xl font-bold"
                        data-testid="dialog-global-vlan-title-text"
                      >
                        {useCustomVlan
                          ? 'VLANs and subnets'
                          : 'Network IP Subnet'}
                      </h3>
                      <div className="flex flex-row items-center justify-start gap-2">
                        <FormSwitch
                          id="use-custom-vlan"
                          name="use-custom-vlan"
                          data-testid="dialog-global-vlan-use-custom-vlan-switch"
                          wrapperClassName="w-auto items-start justify-start gap-2"
                          defaultChecked={useCustomVlan || false}
                          onChange={onChangeUseCustomVlan}
                        />
                        <span className="text-sm text-gray-500">Use VLANs</span>
                      </div>
                    </div>
                  </DialogTitle>
                  <div className="absolute right-4 top-4">
                    <button
                      type="button"
                      onClick={onCancel}
                      data-testid="dialog-global-vlan-close-button"
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <CloseIon className="h-6 w-6" />
                    </button>
                  </div>

                  {useCustomVlan ? (
                    <div
                      data-testid="dialog-global-vlan-table"
                      className="relative overflow-x-auto shadow-md sm:rounded-lg"
                    >
                      <VlanTable />
                    </div>
                  ) : (
                    <div
                      data-testid="dialog-global-ip-subnet"
                      className="relative overflow-x-auto py-2"
                    >
                      <NetworkIpSubnet />
                    </div>
                  )}
                </>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <AlertDialog
        variant="warning"
        title="Warning"
        description="This action will reset the network configuration to the default values."
        confirmLabel="Confirm"
        closeLabel="Cancel"
        open={!!confirmFunction}
        onClose={() => {
          setConfirmFunction(undefined);
          forceRefresh();
        }}
        dataTestId="dialog-global-vlan-alert-dialog"
        onConfirm={() => {
          confirmFunction?.();
          setConfirmFunction(undefined);
        }}
      />
    </>
  );
}

export default DialogVlan;
